import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DatePipe } from '@angular/common';
import { LocationType } from '@shared-features/enums/shared-enums.enum';
import { Customer } from '@features/customers/models/customer.model';
import { User } from '@features/users/models/user.model';
import { Shipment } from '@features/shared-features/models/shipment.model';
import { Receiver } from '@features/receivers/models/receiver.model';
import { LocationModel } from '@features/customers/models/location.model';
import { ShipmentCreationTemplateTypes } from '@shared-features/enums';
import { ShipmentAction } from '@shared-features/enums/shipments.enum';
import { BaseComponent } from '@shared-features/components/base-component/base.component';

@Component({
  selector: 'app-shipping-card',
  templateUrl: './shipping-card.component.html',
  styleUrls: ['./shipping-card.component.scss'],
})
export class ShippingCardComponent extends BaseComponent implements OnChanges {
  @Input() shipment: Shipment;
  @Output() editLocation = new EventEmitter<{
    locationType: LocationType,
    locationOwner: Customer | Receiver,
    location: LocationModel
  }>();

  readonly LocationType = LocationType;
  readonly ShipmentCreationTemplateTypes = ShipmentCreationTemplateTypes;
  private readonly datePipe = new DatePipe(this.translationService.currentLang);

  constructor(private changeRef: ChangeDetectorRef) {
    super(['Shipments']);
  }

  get pickUpContact(): User {
    return this.getUserLocation(this.shipment?.pickUpLocation);
  }

  get dropOffContact(): { firstName?: string, lastName?: string, mobileNumber?: string } {
    const { dropOffUser, contactPerson, dropOffLocation } = this.shipment || {};
    return dropOffUser || contactPerson || this.getUserLocation(dropOffLocation);
  }

  get pickUpAddress(): string {
    return this.locationAddress(this.shipment?.pickUpLocation);
  }

  get pickUpPlace(): string {
    return this.locationPlace(this.shipment?.pickUpLocation);
  }

  get dropOffAddress(): string {
    return this.locationAddress(this.shipment?.dropOffLocation);
  }

  get dropOffPlace(): string {
    return this.locationPlace(this.shipment?.dropOffLocation);
  }

  get pickUpMonth(): string {
    return this.formatDate(this.shipment?.pickUpDate, 'MMM');
  }

  get pickUpDay(): string {
    return this.formatDate(this.shipment?.pickUpDate, 'd');
  }

  get dropOffMonth(): string {
    return this.formatDate(this.shipment?.deliveryDate, 'MMM');
  }

  get dropOffDay(): string {
    return this.formatDate(this.shipment?.deliveryDate, 'd');
  }

  get pickUpCompletedOn(): number {
    return this.findHistoryDate(ShipmentAction.ArrivePickupLocation);
  }

  get dropOffCompletedOn(): number {
    return this.findHistoryDate(ShipmentAction.ArriveDropOffLocation);
  }

  formatContactName(contact: { firstName?: string, lastName?: string }): string {
    return [contact?.firstName, contact?.lastName].filter(Boolean).join(' ');
  }

  formatDateTime(date: Date | number): string {
    return this.formatDate(date, 'dd-MM-yyyy hh:mm aa');
  }

  ngOnChanges({ shipment }: SimpleChanges): void {
    if (shipment && shipment.currentValue !== shipment.previousValue) {
      this.changeRef.markForCheck();
    }
  }

  openEditLocationModal(locationType: LocationType): void {
    this.editLocation.emit({
      locationType,
      locationOwner: locationType === LocationType.Customer ? this.shipment?.customer : this.shipment?.receiver,
      location: locationType === LocationType.Customer ? this.shipment?.pickUpLocation : this.shipment?.dropOffLocation,
    });
  }

  private locationAddress(location: LocationModel): string {
    return location?.address || '';
  }

  private locationPlace(location: LocationModel): string {
    return location?.title || this.locationAddress(location);
  }

  private findHistoryDate(action: ShipmentAction): number {
    return (this.shipment?.history || []).find((item) => item.action === action)?.createdAt;
  }

  private formatDate(date: Date | number, format: string): string {
    return date ? this.datePipe.transform(date, format) : '';
  }

  private getUserLocation(location: LocationModel): User {
    const usersLocations = location?.usersLocations || [];
    const defaultUserLocation = usersLocations.find((user) => user.isDefault);
    const userLocation = usersLocations[0];
    return (defaultUserLocation || userLocation)?.user;
  }
}
